import React, { useEffect, useState, useCallback } from 'react';
import { fetchReservations, cancelReservation } from '../../services/userService';
import OfferPanel from '../../../../shared/components/OfferPanel';

const STATUS_LABELS = {
  PEND:        { text: 'در انتظار پاسخ مالک', color: '#f57c00', bg: '#fff3e0' },
  EXPIRED:     { text: 'منقضی شده',            color: '#757575', bg: '#f5f5f5' },
  NEGOTIATING: { text: 'در حال مذاکره',         color: '#1565c0', bg: '#e3f2fd' },
  PAUSED:      { text: 'متوقف شده',             color: '#6a1b9a', bg: '#f3e5f5' },
  ACCEPTED:    { text: 'پذیرفته شده',           color: '#2e7d32', bg: '#e8f5e9' },
  REJECTED:    { text: 'رد شده',                color: '#c62828', bg: '#ffebee' },
  COMPLETED:   { text: 'نهایی شده',             color: '#2e7d32', bg: '#e8f5e9' },
  CANCELLED:   { text: 'لغو شده',               color: '#757575', bg: '#f5f5f5' },
};

const OFFER_ACTIVE_STATUSES = ['NEGOTIATING', 'ACCEPTED'];

function ReservationCard({ item, onReload }) {
  const [cancelling, setCancelling] = useState(false);
  const [cancelNote, setCancelNote] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [error, setError] = useState(null);

  const badge = STATUS_LABELS[item.status] || { text: item.status, color: '#333', bg: '#eee' };
  const address = item.listing_info?.address || `آگهی #${item.listing}`;
  const listingType = item.listing_info?.listing_type || 'SALE';

  const canCancel = ['PEND', 'NEGOTIATING', 'PAUSED'].includes(item.status);

  const handleCancel = async () => {
    setCancelling(true);
    setError(null);
    try {
      await cancelReservation(item.id, cancelNote);
      setShowCancel(false);
      if (onReload) onReload();
    } catch (e) {
      setError(e?.response?.data?.detail || 'خطا در لغو درخواست');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div style={{
      border: '1px solid #e0e0e0', borderRadius: 8, padding: 12,
      marginBottom: 16, background: '#fafafa',
    }} dir="rtl">
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{address}</span>
        <span style={{
          padding: '2px 10px', borderRadius: 12, fontSize: 12,
          background: badge.bg, color: badge.color,
        }}>
          {badge.text}
        </span>
      </div>

      {item.request_note && (
        <p style={{ fontSize: 13, color: '#555', margin: '4px 0' }}>
          یادداشت: {item.request_note}
        </p>
      )}
      {item.rejection_note && (
        <p style={{ fontSize: 13, color: '#c62828', margin: '4px 0' }}>
          دلیل رد: {item.rejection_note}
        </p>
      )}
      <p style={{ fontSize: 12, color: '#9e9e9e', margin: '4px 0 8px' }}>
        {new Date(item.created_at).toLocaleDateString('fa-IR')}
      </p>

      {/* Offer tracking — shown while negotiating or after acceptance */}
      {OFFER_ACTIVE_STATUSES.includes(item.status) && (
        <OfferPanel
          reservationId={item.id}
          myRole="buyer"
          listingType={listingType}
          onActioned={onReload}
        />
      )}

      {/* Cancel button */}
      {canCancel && !showCancel && (
        <button onClick={() => setShowCancel(true)} style={{
          marginTop: 8, padding: '4px 12px', borderRadius: 4,
          background: '#c62828', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12,
        }}>
          لغو درخواست
        </button>
      )}

      {showCancel && (
        <div style={{ marginTop: 8 }}>
          <textarea rows={2} placeholder="دلیل لغو (اختیاری)" value={cancelNote}
            onChange={e => setCancelNote(e.target.value)}
            style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid #ddd', fontSize: 12, boxSizing: 'border-box' }} />
          {error && <p style={{ color: '#c62828', fontSize: 12 }}>{error}</p>}
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button onClick={handleCancel} disabled={cancelling} style={{
              padding: '4px 12px', borderRadius: 4, background: '#c62828',
              color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12,
            }}>
              {cancelling ? '...' : 'تأیید لغو'}
            </button>
            <button onClick={() => { setShowCancel(false); setCancelNote(''); }} style={{
              padding: '4px 12px', borderRadius: 4, background: '#9e9e9e',
              color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12,
            }}>
              انصراف
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ReservationsPanel() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchReservations()
      .then(data => setReservations(Array.isArray(data) ? data : []))
      .catch(err => setError(err.message || 'خطا در دریافت رزروها'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 16 }}>در حال بارگذاری...</div>;
  if (error) return <div style={{ padding: 16, color: '#c62828' }}>خطا: {error}</div>;

  const active = reservations.filter(r => !['CANCELLED', 'REJECTED', 'EXPIRED', 'COMPLETED'].includes(r.status));
  const past = reservations.filter(r => ['CANCELLED', 'REJECTED', 'EXPIRED', 'COMPLETED'].includes(r.status));

  return (
    <div style={{ padding: 16 }} dir="rtl">
      <h2>رزروهای من</h2>

      {reservations.length === 0 && (
        <p style={{ color: '#757575' }}>رزروی یافت نشد.</p>
      )}

      {active.map(item => (
        <ReservationCard key={item.id} item={item} onReload={load} />
      ))}

      {past.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: '#757575' }}>
            رزروهای قبلی ({past.length})
          </summary>
          <div style={{ marginTop: 8 }}>
            {past.map(item => (
              <ReservationCard key={item.id} item={item} onReload={load} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
